import {DotFillIcon} from '@primer/octicons-react'
import {memo} from 'react'
import type {SessionState} from '../types/session'
import {getLeadingVisualIconAttributes} from '../utils/get-leading-visual-icon-attributes'
import styles from './TaskLeadingVisual.module.css'

export interface TaskLeadingVisualProps {
  isOptimistic?: boolean
  prState?: string
  prInMergeQueue?: boolean
  sessionState: SessionState
  isArchived?: boolean
  className?: string
}

/**
 * Displays a leading visual icon for a task/session based on its current state.
 * Only handles state-based icons (PR status, session status, etc.).
 * For agent-type-specific icons, callers should render AgentProfile.slots.LeadingIcon directly.
 *
 * This component is memoized to prevent animated icons (like CheckSpinner) from resetting
 * when other tasks in a list update.
 */
// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
export const TaskLeadingVisual = memo(function TaskLeadingVisual({
  isOptimistic = false,
  prState,
  prInMergeQueue,
  sessionState,
  isArchived,
  className,
}: TaskLeadingVisualProps) {
  const iconAttributes = isOptimistic
    ? {icon: DotFillIcon, color: 'var(--fgColor-attention)', 'data-testid': 'icon-dot'}
    : getLeadingVisualIconAttributes(prState, prInMergeQueue, sessionState, isArchived)

  const Icon = iconAttributes.icon

  return (
    <div
      className={`${styles.iconWrapper} ${className ?? ''}`}
      style={{
        color: iconAttributes.color,
        // Add margin-top for spinning icon to align properly
        marginTop: sessionState === 'in_progress' && !isOptimistic ? '3px' : undefined,
      }}
      data-testid={iconAttributes['data-testid']}
    >
      <Icon />
    </div>
  )
})
